import React, { useRef, useState } from 'react';
import { Paperclip, X, Loader2, Image, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { useWorkspace } from '../../context/WorkspaceContext';
import { getUploadErrorMessage } from '../../lib/utils';

const FileUploader = ({ onFileUploaded, channelId, conversationId, disabled }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const { refreshStorage } = useWorkspace();

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        setUploading(true);
        const uploaded = [];

        for (const file of files) {
            try {
                // Validate file size (50MB max)
                const maxSize = 50 * 1024 * 1024;
                if (file.size > maxSize) {
                    toast.error(`${file.name} terlalu besar. Maksimal 50MB`);
                    continue;
                }

                const formData = new FormData();
                formData.append('file', file);
                if (channelId) formData.append('channel_id', channelId);
                if (conversationId) formData.append('conversation_id', conversationId);

                const token = localStorage.getItem('token');
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                const response = await axios.post(
                    `${backendUrl}/api/chat/upload`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                uploaded.push(response.data);
                toast.success(`${file.name} berhasil diupload`);
            } catch (error) {
                console.error('Upload error:', error);
                toast.error(getUploadErrorMessage(error));
            }
        }

        setUploadedFiles(prev => [...prev, ...uploaded]);
        setUploading(false);

        if (uploaded.length > 0) refreshStorage();

        // Notify parent component
        if (onFileUploaded && uploaded.length > 0) {
            onFileUploaded(uploaded);
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    return (
        <div>
            <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                disabled={disabled || uploading}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
            />
            
            {/* Upload Button */}
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || uploading}
            >
                {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Paperclip className="w-4 h-4" />
                )}
            </Button>

            {/* Uploaded Files Preview */}
            {uploadedFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {uploadedFiles.map(file => (
                        <div
                            key={file.id}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md text-sm"
                        >
                            {file.file_type === 'image' ? (
                                <Image className="w-4 h-4 text-gray-600" />
                            ) : (
                                <FileText className="w-4 h-4 text-gray-600" />
                            )}
                            <span className="text-gray-700 truncate max-w-[150px]">
                                {file.filename}
                            </span>
                            <button
                                onClick={() => removeFile(file.id)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUploader;
